// THE SINGLE-FILE GOLDEN DROP POINT.
//
// The CIO, 2026-08-11: *"the CSV to be uploaded will be ONE file that contains ALL of the
// performance level data (for portfolios, portfolio holdings, benchmarks, benchmark constituents,
// ARC, AFI & Investment Association). Make sure the front end works with this as I note at the
// moment it gives a dropdown."*
//
// THE DROPDOWN IS GONE. It asked the human "what is this file?" — a question the file answers
// itself, and answers better: `src/features/investment-reporting` reads the FE preamble, the row
// names and the peer ladder and classifies all 170 rows without being told anything.
//
// THE ORDER OF OPERATIONS IS THE WHOLE POINT:
//
//   1. drop        — one file. No kind picker, no basis picker (the file states its own currency).
//   2. classify    — ENTIRELY CLIENT-SIDE. Nothing has left the browser at this stage.
//   3. REVIEW      — counts, the per-level tie check, the unclassified residue by name, warnings,
//                    and the basis statement. Shown BEFORE anything is committed.
//   4. refuse      — if the classifier raised a refusal the Confirm is NOT OFFERED. Not disabled
//                    with a shrug: absent, with the reason in English and its consequence.
//   5. confirm     — only then, and only on an explicit press, do the bytes go anywhere.
//
// WHY THE UPLOAD FANS OUT INTO MORE THAN ONE CALL. `reporting_golden_sources.kind` is NOT NULL
// with a closed 12-value CHECK and is part of the natural key UNIQUE(period_label, kind,
// content_sha256); `reporting_snapshot_precheck` then aggregates DISTINCT kind and requires each
// required kind present. The database cannot represent a "combined" source. So one dropped file
// becomes N source rows — the SAME BYTES posted once per kind, which is exactly the mechanism
// golden-csv-contract-v1 §8.1 already specifies for the two-kinds case ("the same bytes are
// dropped twice, once per kind — identical sha256 is the proof it is one file"). Here it is the
// machine doing the re-dropping instead of the human.
//
// WHAT LANDS, AND WHY IT IS NOW THE WHOLE FILE. `reporting-golden-ingest` used to restrict the
// FE-native parse path to FE_KINDS = {fe_portfolio_returns, fe_benchmark_returns} (index.ts:369)
// and HARD REJECT every other kind on a file of this shape at GATE 0, because it had no crosswalk
// for them and would not guess one. It has them now: the peer ladder, the fund roster and the IA
// sector names are all crosswalks this classifier already carries, and the CIO's instruction of
// 2026-08-11 was to widen the ingest so the whole file lands. So the fan-out is five kinds, and
// `fe_ia_sector_returns` is the twelfth member of the `reporting_golden_sources.kind` CHECK —
// that exact literal, per supabase/migrations/20260811120000_widen_golden_kinds.sql.
//
// ⚠️ KIND_PLAN BELOW IS A MIRROR OF THE SERVER'S FE_KINDS, NOT A SECOND OPINION. The two must move
// together in one change: widening here without widening index.ts:369 posts requests that come
// back 422, and widening there without the migration posts a `kind` the CHECK will reject.
//
// ⚠️ DUPLICATES. The classifier collapses rows identical in BOTH name and value (the three
// byte-identical Vanguard US Government Bond lines) and REFUSES a file where one name carries two
// different values. That happens to what is READ from the bytes; the bytes themselves are uploaded
// exactly as dropped, because their sha256 is the file's identity in the ledger and rewriting them
// would break the only proof that what was stored is what FE produced.

import { useCallback, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  CheckCircle2,
  FileUp,
  Loader2,
  RotateCcw,
  ShieldCheck,
  UploadCloud,
  X,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toneText } from "./labels";
import { CombinedIntakeReview } from "./CombinedIntakeReview";
import {
  localDropError,
  readCsvPayload,
  useGoldenDrop,
  type CsvPayload,
  type GoldenDropOutcome,
} from "@/hooks/investment-reporting/useGoldenIngest";
import { PERIOD_LABEL_RX } from "@/lib/investment-reporting/period";
import { classifyCombinedExport, PEER_HOUSE_LABEL } from "@/features/investment-reporting";
import type {
  CbamFundRosterRow,
  FeCombinedClassification,
  FeRowClass,
} from "@/features/investment-reporting/types";

// ---------------------------------------------------------------------------
// The fan-out plan: which class of row belongs to which golden `kind`.
//
// `landable` mirrors FE_KINDS in supabase/functions/reporting-golden-ingest/index.ts:369. It is a
// MIRROR, not a second opinion — if that set moves, this moves with it, in the same change.
//
// Every class the classifier recognises except the residue is now owned by a kind. The residue is
// deliberately unowned: see UNLANDABLE_NOTE.
// ---------------------------------------------------------------------------
interface KindPlanSpec {
  kind: string;
  label: string;
  classes: FeRowClass[];
  landable: boolean;
  /** Why it cannot land. Shown verbatim; null when it can. */
  blockedReason: string | null;
}

const KIND_PLAN: KindPlanSpec[] = [
  {
    kind: "fe_portfolio_returns",
    label: "Portfolio returns (P1–P5, R1–R5, MM)",
    classes: ["cbam_book"],
    landable: true,
    blockedReason: null,
  },
  {
    kind: "fe_benchmark_returns",
    label: "Benchmark returns (CBAM I–V, AFI, ARC)",
    classes: ["cbam_benchmark", "external_comparator"],
    landable: true,
    blockedReason: null,
  },
  {
    kind: "composite_constituents",
    label: "Peer constituents (9 houses × 5 levels)",
    classes: ["peer_constituent"],
    landable: true,
    blockedReason: null,
  },
  {
    kind: "fe_fund_returns",
    label: "Holdings (fund-level returns)",
    classes: ["holding"],
    landable: true,
    blockedReason: null,
  },
  {
    // The twelfth `kind`. It lands the IA category returns VERBATIM by name — no crosswalk to a
    // CBAM sector is applied here, because that crosswalk belongs at the 10-way SECTOR_CATEGORIES
    // grain in services/reporting-compute/attribution.py and is a judgement nobody has made yet.
    // Storing the names as FE wrote them keeps that decision open instead of pre-empting it.
    kind: "fe_ia_sector_returns",
    label: "IA sector returns",
    classes: ["ia_sector"],
    landable: true,
    blockedReason: null,
  },
];

// WHAT STILL DOES NOT LAND, stated honestly rather than rounded off to "everything lands".
//
// The unclassified residue. On the real export that is ONE row — "Pounds Sterling in GB", FE's
// currency baseline. It is not an instrument, it matches nothing in ba_cbam_funds, and its 0.00 is
// equally consistent with "baseline artefact" and "the CASH pseudo-fund". No kind claims it,
// because binding it to CASH would mint a holding carrying a return nobody reported.
const UNLANDABLE_NOTE =
  "No kind claims these rows. On the FE export this is the \"Pounds Sterling\" currency baseline — "
  + "not an instrument, and deliberately left unbound: treating it as the CASH pseudo-fund would "
  + "create a holding with a return nobody reported. It is read, named and checked here, and it is "
  + "not sent anywhere.";

// ---------------------------------------------------------------------------
// sha256 of the exact bytes, for the reviewer. The server computes its own over the bytes it
// receives and that hash is the file's identity; showing ours lets the two be compared by eye.
// Never fatal — a browser without crypto.subtle just shows no hash.
// ---------------------------------------------------------------------------
async function sha256Hex(file: File): Promise<string | null> {
  try {
    if (!globalThis.crypto?.subtle) return null;
    const buf = await file.arrayBuffer();
    const digest = await globalThis.crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// The estate roster, for binding HOLDING lines to ba_cbam_funds.
//
// Injected into the classifier rather than looked up inside it, so the classifier stays pure and
// testable. A roster that fails to load is NOT fatal: holdings then report as unmatched, which is
// a warning, never a refusal — the file is still fully reviewable.
// ---------------------------------------------------------------------------
function useCbamFundRoster() {
  return useQuery({
    queryKey: ["ba_cbam_funds_roster"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<CbamFundRosterRow[]> => {
      const { data, error } = await supabase
        .from("ba_cbam_funds")
        .select("id, fe_fund_name, fund_manager, fund_name, fund_class, isin, is_current");
      if (error) throw error;
      return (data ?? []) as CbamFundRosterRow[];
    },
  });
}

type Phase = "empty" | "reading" | "reviewing" | "uploading" | "landed";

interface LandingResult {
  kind: string;
  label: string;
  outcome: GoldenDropOutcome;
  /** Set once the reviewer attests this landed source. */
  attested: boolean;
  attestError: string | null;
  attesting: boolean;
}

export function CombinedCsvDropZone({
  cycleId,
  periodLabel,
  asOfDate,
  className,
}: {
  cycleId: string | null | undefined;
  periodLabel: string | null | undefined;
  asOfDate: string | null | undefined;
  className?: string;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [payload, setPayload] = useState<CsvPayload | null>(null);
  const [sha, setSha] = useState<string | null>(null);
  const [classification, setClassification] = useState<FeCombinedClassification | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("empty");
  const [results, setResults] = useState<LandingResult[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const roster = useCbamFundRoster();
  const drop = useGoldenDrop(cycleId);

  const label = (periodLabel ?? "").trim();
  // The ingest constrains period_label to YYYY-MM / YYYY-Qn (it becomes a Storage object-key
  // segment and part of the drop's unique key). reporting_cycles carries no such CHECK, so a
  // cycle CAN hold a label the ingest will refuse — say so up front rather than letting the
  // upload fail after the reviewer has read the whole file.
  const labelUsable = PERIOD_LABEL_RX.test(label);
  const asOf = (asOfDate ?? "").slice(0, 10);
  // The roster must have SETTLED before a file is classified. Classifying while it is still in
  // flight would report all 46 holdings as unmatched and put a wall of warnings in front of the
  // reviewer that says nothing about the file. A roster that FAILED is fine to proceed on (the
  // unmatched warnings are then true, and the banner below says why); a roster still loading is
  // not. The query is cached for ten minutes, so this is a first-visit wait only.
  const rosterSettled = !roster.isLoading;
  const canDrop = labelUsable && !!asOf && rosterSettled;

  const reset = useCallback(() => {
    setFile(null);
    setPayload(null);
    setSha(null);
    setClassification(null);
    setReadError(null);
    setResults([]);
    setPhase("empty");
  }, []);

  // ── 1-3: read, classify, and STOP. Nothing leaves the browser here. ──────────
  const takeFile = useCallback(
    async (picked: File | null | undefined) => {
      if (!picked) return;
      setFile(picked);
      setResults([]);
      setReadError(null);
      setClassification(null);
      setPhase("reading");

      let text: string;
      let csvPayload: CsvPayload;
      try {
        // Same byte-fidelity discipline as the old drop point: decode fatally so a non-UTF-8
        // file throws instead of silently landing replacement characters under a hash that
        // no longer matches the file on disk.
        csvPayload = await readCsvPayload(picked);
        // The base64 branch is taken precisely BECAUSE the bytes are not valid UTF-8 — typically a
        // Windows-1252 export carrying a £ or an en-dash. Decoding those as UTF-8 would put
        // replacement characters into fund names and make them unmatchable, so the local decode
        // for CLASSIFICATION uses windows-1252. The UPLOAD is unaffected: it still sends the
        // original base64, byte-exact, so the server's hash is the hash of the file on disk.
        text =
          csvPayload.csv_text != null
            ? csvPayload.csv_text
            : new TextDecoder("windows-1252").decode(
                Uint8Array.from(atob(csvPayload.csv_base64 ?? ""), (ch) => ch.charCodeAt(0)),
              );
      } catch (e: any) {
        setReadError(`The file could not be read: ${e?.message ?? String(e)}`);
        setPhase("empty");
        return;
      }

      const hash = await sha256Hex(picked);

      let result: FeCombinedClassification;
      try {
        result = classifyCombinedExport(text, {
          fundRoster: roster.data ?? undefined,
        });
      } catch (e: any) {
        // The classifier is specified never to throw on bad data. If it ever does, that is a bug
        // in the classifier and it must read as one — not as "your file is bad".
        setReadError(
          `The classifier failed on this file: ${e?.message ?? String(e)}. That is a defect in the `
            + "classifier, not necessarily in the file. Nothing has been uploaded.",
        );
        setPhase("empty");
        return;
      }

      setPayload(csvPayload);
      setSha(hash);
      setClassification(result);
      setPhase("reviewing");
    },
    [roster.data],
  );

  // ── The fan-out plan for THIS file ──────────────────────────────────────────
  const plan = useMemo(() => {
    if (!classification) return [];
    return KIND_PLAN.map((spec) => {
      const rows = spec.classes.reduce((n, k) => n + (classification.counts?.[k] ?? 0), 0);
      return { ...spec, rows };
    }).filter((p) => p.rows > 0);
  }, [classification]);

  const landablePlan = plan.filter((p) => p.landable);
  const blockedPlan = plan.filter((p) => !p.landable);
  // The residue: rows no kind claims. Counted from the classification rather than assumed, so if a
  // future export carries something new and unrecognised it appears here instead of vanishing.
  const unlandableRows = classification?.counts?.unclassified ?? 0;
  const plannedRows = landablePlan.reduce((n, p) => n + p.rows, 0);
  const dedupe = classification?.dedupe;

  const refusals = classification?.refusals ?? [];
  const blocked = refusals.length > 0;

  // ── 5: the confirm. Sequential, one call per kind, identical bytes each time. ─
  async function confirmUpload() {
    if (!classification || blocked || !canDrop || !payload || phase === "uploading") return;
    setPhase("uploading");
    const acc: LandingResult[] = [];
    for (const p of landablePlan) {
      let outcome: GoldenDropOutcome;
      try {
        outcome = await drop({
          cycleId,
          periodLabel: label,
          asOfDate: asOf,
          kind: p.kind,
          // The file states its own currency on line 2 and the ingest hard-compares the two at
          // GATE 6. Taking it from the file rather than a picker removes the only way a human
          // could have got it wrong.
          basis: classification.provenance?.currencyCode ?? "GBP",
          filename: file?.name ?? null,
          payload,
        });
      } catch (e: any) {
        outcome = localDropError(p.kind, e?.message ?? String(e));
      }
      acc.push({
        kind: p.kind,
        label: p.label,
        outcome,
        attested: false,
        attestError: null,
        attesting: false,
      });
      setResults([...acc]);
    }
    setPhase("landed");
  }

  // ── Attestation. A SEPARATE, EXPLICIT act — see the note in the panel below. ──
  async function attestOne(idx: number) {
    const target = results[idx];
    if (!target?.outcome?.source_id || target.attested || target.attesting) return;
    setResults((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, attesting: true, attestError: null } : r)),
    );
    try {
      // attest_golden_source(_source_id uuid, _note text) -> void, SECURITY DEFINER. Cast through
      // `any` because the generated Supabase types do not know the reporting RPCs yet — the same
      // pattern useReportingAccess uses for can_review_reporting.
      const { error } = await (supabase as any).rpc("attest_golden_source", {
        _source_id: target.outcome.source_id,
        _note: `Attested at the combined-CSV drop point for ${label}`,
      });
      if (error) throw error;
      setResults((prev) =>
        prev.map((r, i) => (i === idx ? { ...r, attested: true, attesting: false } : r)),
      );
    } catch (e: any) {
      setResults((prev) =>
        prev.map((r, i) =>
          i === idx
            ? { ...r, attesting: false, attestError: e?.message ?? String(e) }
            : r,
        ),
      );
    }
  }

  const landedOk = results.filter(
    (r) => r.outcome.state === "parsed" || r.outcome.state === "duplicate",
  );

  return (
    <div className={cn("space-y-3", className)}>
      {!labelUsable && (
        <Card className="p-3 border-destructive/40">
          <p className="font-body text-xs text-destructive">
            The period label on this cycle{" "}
            {label ? <span className="font-mono">({label})</span> : <span>(empty)</span>} cannot be
            used for a golden-CSV drop. The ingest requires YYYY-MM (2026-07) or YYYY-Qn (2026-Q2),
            because the label becomes part of the identity of every stored file. This cycle needs a
            label in one of those two shapes before anything can be dropped against it.
          </p>
        </Card>
      )}

      {/* ── The drop surface ─────────────────────────────────────────────────── */}
      {phase === "empty" && (
        <Card
          className={cn(
            "p-6 border-dashed flex flex-col items-center text-center gap-2 transition-colors",
            dragging && "border-primary bg-primary/5",
            !canDrop && "opacity-60",
          )}
          onDragOver={(e) => {
            e.preventDefault();
            if (canDrop) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (canDrop) void takeFile(e.dataTransfer?.files?.[0]);
          }}
        >
          <UploadCloud className="h-7 w-7 text-muted-foreground" aria-hidden />
          <p className="font-body text-sm text-foreground">Drop the combined FE export here</p>
          <p className="font-body text-xs text-muted-foreground max-w-lg">
            One file, containing everything — the P and R books, the CBAM benchmarks, the nine peer
            houses, ARC, AFI, the IA sectors and the individual holdings. You are not asked what it
            is; it is read and classified here, and you see the result before anything is uploaded.
            It lands against <span className="font-mono">{label || "—"}</span>, as of{" "}
            {asOf || "—"} — taken from this cycle, never retyped.
          </p>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              void takeFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-1"
            disabled={!canDrop}
            onClick={() => fileInput.current?.click()}
          >
            <FileUp className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            Choose file
          </Button>
          {roster.isLoading && (
            <p className="font-body text-[11px] text-muted-foreground">
              Loading the fund roster so holdings can be identified…
            </p>
          )}
          {roster.isError && (
            <p className="font-body text-[11px] text-amber-600 dark:text-amber-400 max-w-lg">
              The fund roster could not be loaded, so holdings will be reported as unmatched. That
              is a warning, not a blocker — everything else still checks.
            </p>
          )}
        </Card>
      )}

      {readError && (
        <Card className="p-3 border-destructive/40">
          <p className="font-body text-xs text-destructive">{readError}</p>
        </Card>
      )}

      {phase === "reading" && (
        <Card className="p-6 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
          <span className="font-body text-sm text-muted-foreground">
            Reading and classifying {file?.name ?? "the file"}…
          </span>
        </Card>
      )}

      {/* ── The review ───────────────────────────────────────────────────────── */}
      {classification && phase !== "reading" && (
        <>
          <div className="flex items-center justify-between gap-3">
            <p className="font-body text-sm text-foreground min-w-0 truncate">
              {phase === "landed" ? "Landed from" : "Reviewing"}{" "}
              <span className="font-medium">{file?.name}</span>
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px] shrink-0"
              disabled={phase === "uploading"}
              onClick={reset}
            >
              <X className="h-3 w-3 mr-1" aria-hidden />
              {phase === "landed" ? "Drop another file" : "Discard"}
            </Button>
          </div>

          <CombinedIntakeReview
            classification={classification}
            fileName={file?.name}
            byteSize={payload?.byte_size ?? file?.size}
            sha256={sha}
            houseLabel={(h) => (PEER_HOUSE_LABEL as Record<string, string>)[h] ?? h}
          />

          {/* ── The landing plan + the confirm ──────────────────────────────── */}
          {phase !== "landed" && (
            <Card className="p-4 space-y-3">
              <h4 className="font-heading text-sm font-medium text-foreground">
                What pressing confirm would do
              </h4>

              {blocked ? (
                <p className="font-body text-sm text-destructive">
                  Nothing — the file was refused above, so there is no upload to confirm.
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full font-body text-sm">
                      <thead>
                        <tr className="text-muted-foreground text-xs">
                          <th className="text-left font-normal py-1">Rows</th>
                          <th className="text-left font-normal py-1">Landed as</th>
                          <th className="text-right font-normal py-1">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {landablePlan.map((p) => (
                          <tr key={p.kind} className="border-t">
                            <td className="py-1.5 text-foreground">{p.label}</td>
                            <td className="py-1.5 font-mono text-xs text-muted-foreground">
                              {p.kind}
                            </td>
                            <td className="py-1.5 text-right tabular-nums text-foreground">
                              {p.rows}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="font-body text-[11px] text-muted-foreground">
                    <span className="text-foreground">
                      {plannedRows} of the {classification.counts?.total ?? 0} rows
                    </span>{" "}
                    kept from this file would be landed, across {landablePlan.length} upload
                    {landablePlan.length === 1 ? "" : "s"} of the SAME bytes, one per kind — the
                    database keys a source on (period, kind, content hash) and cannot represent a
                    combined file, so an identical sha256 across the rows is what proves they are
                    one export.
                  </p>
                  {dedupe && dedupe.rowsRemoved > 0 && (
                    <p className="font-body text-[11px] text-muted-foreground">
                      The file itself is uploaded byte-for-byte as dropped — its sha256 is its
                      identity in the ledger, so it is never rewritten. The{" "}
                      {dedupe.rowsRemoved} identical duplicate row
                      {dedupe.rowsRemoved === 1 ? "" : "s"} listed above{" "}
                      {dedupe.rowsRemoved === 1 ? "is" : "are"} removed from what is READ out of
                      those bytes; the destination keys a row on (source, entity, metric, as-of
                      date), so an identical row cannot land twice either way.
                    </p>
                  )}
                  {/* The basis is DECLARED to the server and hard-compared against the file's own
                      currency line. It is taken from the file rather than a picker, so there is no
                      way to declare one currency and upload another — but the reviewer should still
                      see which value is being asserted on their behalf. */}
                  <p className="font-body text-[11px] text-muted-foreground">
                    Declared basis:{" "}
                    <span className="font-mono text-foreground">
                      {classification.provenance?.currencyCode ?? "GBP"}
                    </span>
                    {classification.provenance?.currencyCode
                      ? ` — read from the file's own currency line (${classification.provenance.currency}).`
                      : " — the file's currency line could not be read, so the default is being"
                        + " declared. If that is not what the file says, the server will reject it"
                        + " rather than accept the wrong basis."}
                  </p>

                  {/* WHAT IS NOT UPLOADED. This block used to carry three whole classes — the
                      peer constituents, the holdings and the IA sectors — because the server
                      served two kinds. It serves five now, so what is left here is the honest
                      remainder: the rows no kind claims, and nothing else. If that list is empty,
                      the section does not render, because there is then nothing to say. */}
                  {(blockedPlan.length > 0 || unlandableRows > 0) && (
                    <div className="space-y-1.5 border-t pt-2">
                      <p className="font-body text-xs font-medium text-amber-600 dark:text-amber-400">
                        Read and checked, but NOT uploaded:
                      </p>
                      <ul className="space-y-1.5">
                        {blockedPlan.map((p) => (
                          <li key={p.kind} className="flex items-start gap-1.5">
                            <AlertTriangle
                              className={cn("h-3 w-3 mt-1 shrink-0", toneText("attention"))}
                              aria-hidden
                            />
                            <span className="font-body text-xs text-muted-foreground">
                              <span className="text-foreground">
                                {p.label} ({p.rows} rows)
                              </span>{" "}
                              — {p.blockedReason}
                            </span>
                          </li>
                        ))}
                        {unlandableRows > 0 && (
                          <li className="flex items-start gap-1.5">
                            <AlertTriangle
                              className={cn("h-3 w-3 mt-1 shrink-0", toneText("attention"))}
                              aria-hidden
                            />
                            <span className="font-body text-xs text-muted-foreground">
                              <span className="text-foreground">
                                Unclassified ({unlandableRows} row
                                {unlandableRows === 1 ? "" : "s"})
                              </span>{" "}
                              — {UNLANDABLE_NOTE} {unlandableRows === 1 ? "It is" : "They are"}{" "}
                              named above, under “Rows nothing could be made of”.
                            </span>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button
                  type="button"
                  disabled={blocked || !canDrop || phase === "uploading" || landablePlan.length === 0}
                  onClick={confirmUpload}
                  title={
                    blocked
                      ? "The file was refused — fix the export and drop it again"
                      : "Upload these bytes, once per kind"
                  }
                >
                  {phase === "uploading" ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <UploadCloud className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                      Confirm and upload
                    </>
                  )}
                </Button>
                {!blocked && (
                  <span className="font-body text-[11px] text-muted-foreground">
                    Nothing has left this browser yet.
                  </span>
                )}
              </div>
            </Card>
          )}

          {/* ── Per-kind outcomes ──────────────────────────────────────────── */}
          {results.length > 0 && (
            <Card className="p-4 space-y-3">
              <h4 className="font-heading text-sm font-medium text-foreground">What was landed</h4>
              <ul className="space-y-2.5">
                {results.map((r, i) => (
                  <li key={r.kind} className="space-y-1 border-t pt-2 first:border-t-0 first:pt-0">
                    <div className="flex items-center gap-1.5 text-sm">
                      {r.outcome.state === "parsed" || r.outcome.state === "duplicate" ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" aria-hidden />
                      ) : (
                        <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" aria-hidden />
                      )}
                      <span className="text-foreground font-medium">{r.label}</span>
                      <span className="font-mono text-[11px] text-muted-foreground">{r.kind}</span>
                    </div>

                    {r.outcome.state === "parsed" && (
                      <p className="font-body text-[11px] text-muted-foreground">
                        Parsed —{" "}
                        {r.outcome.rows_inserted != null
                          ? `${r.outcome.rows_inserted} rows`
                          : "landed"}
                        {r.outcome.sha256 ? ` · sha256 ${r.outcome.sha256.slice(0, 16)}…` : ""}
                      </p>
                    )}
                    {r.outcome.state === "duplicate" && (
                      <p className="font-body text-[11px] text-muted-foreground">
                        Already landed — these exact bytes are recorded for this period and kind, so
                        nothing was re-parsed. Re-dropping is always safe.
                      </p>
                    )}
                    {(r.outcome.state === "rejected" || r.outcome.state === "error") && (
                      <p className="font-body text-[11px] text-destructive break-words">
                        {r.outcome.reject_gate ? `${r.outcome.reject_gate}: ` : ""}
                        {r.outcome.reject_reason ?? "No reason returned."} Nothing was landed for
                        this kind.
                      </p>
                    )}

                    {/* Attestation — a SEPARATE act from landing the bytes, deliberately. Landing
                        says "these are the bytes FE gave me". Attesting says "I have looked at
                        them and I stand behind them". reporting_snapshot_precheck requires the
                        second, and nothing else in the island offers it. */}
                    {(r.outcome.state === "parsed" || r.outcome.state === "duplicate") &&
                      r.outcome.source_id && (
                        <div className="flex flex-wrap items-center gap-2 pt-0.5">
                          {r.attested ? (
                            <span className="inline-flex items-center gap-1 font-body text-[11px] text-primary">
                              <ShieldCheck className="h-3 w-3" aria-hidden /> Attested by you
                            </span>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-[11px]"
                              disabled={r.attesting}
                              onClick={() => void attestOne(i)}
                            >
                              {r.attesting ? (
                                <>
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" aria-hidden />
                                  Attesting…
                                </>
                              ) : (
                                <>
                                  <ShieldCheck className="h-3 w-3 mr-1" aria-hidden />
                                  Attest this source
                                </>
                              )}
                            </Button>
                          )}
                          {r.attestError && (
                            <span className="font-body text-[11px] text-destructive break-words">
                              {r.attestError}
                            </span>
                          )}
                        </div>
                      )}
                  </li>
                ))}
              </ul>

              {results.some((r) => r.outcome.state === "rejected" || r.outcome.state === "error") && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => {
                    setResults([]);
                    setPhase("reviewing");
                  }}
                >
                  <RotateCcw className="h-3 w-3 mr-1" aria-hidden />
                  Try again
                </Button>
              )}

              {/* The honest end of the story. Attesting is necessary but NOT sufficient: the
                  snapshot precheck also requires every required kind to be at
                  ingest_status='validated', and nothing anywhere writes that value —
                  reporting-golden-ingest stops at 'parsed'. Saying so here is the difference
                  between a reviewer who knows the cycle is still blocked and one who finds out
                  at the gate. */}
              {landedOk.length > 0 && (
                <p className="font-body text-[11px] text-muted-foreground border-t pt-2">
                  Landing and attesting these sources does not on its own let the cycle run. The
                  snapshot precheck also requires each source to reach{" "}
                  <span className="font-mono">ingest_status='validated'</span>, and no code path in
                  the estate writes that value yet — the ingest stops at{" "}
                  <span className="font-mono">'parsed'</span>. The cycle will still fail its
                  snapshot stage until that is built.
                </p>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
